'use client'

import { useTranslations } from 'next-intl'
import { type KeyboardEvent } from 'react'

import { useEnhancedFormContext } from '@/shared/lib'
import { Button, Form } from '@/shared/ui'

import { BankSelector } from './bank-selector'
import { ErrorMessage } from './error-message'
import { useBankCodes } from '../api/get-bank-codes'
import { useAccountVerification } from '../lib/account-verification'

type Props = {
  onEditButtonClick: () => void
}

export const AccountForm = ({ onEditButtonClick }: Props) => {
  const t = useTranslations('deposit_information')

  const {
    clearErrors,
    formState: { errors },
    setValue,
  } = useEnhancedFormContext()

  const { banks } = useBankCodes()

  const {
    verifyAccount,
    isAccountVerified,
    isVerificationFailed,
    markAsUnverified,
  } = useAccountVerification()

  const handleAccountInputChange = () => {
    if (errors?.bankAccount) {
      clearErrors('bankAccount')
    }

    if (errors?.accountHolder) {
      setValue('accountHolder', '')
      clearErrors(['root.accountServerError', 'accountHolder'])
      markAsUnverified()
    }
  }

  const handleAccountInputKeyDown = (
    event: KeyboardEvent<HTMLInputElement>,
  ) => {
    if (event.key === 'Enter') {
      void verifyAccount()
    }
  }

  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <BankSelector banks={banks} readOnly={isAccountVerified} />
        <Form.Field name="bankAccount" className="flex-grow">
          <Form.TextField
            type="number"
            placeholder={t('account_number.placeholder')}
            fullWidth
            readOnly={isAccountVerified}
            onChange={handleAccountInputChange}
            onKeyDown={handleAccountInputKeyDown}
          />
        </Form.Field>
      </div>

      <Form.Field name="accountHolder" className="flex-grow">
        <Form.TextField
          placeholder={t('account_holder.placeholder')}
          fullWidth
          readOnly={!isVerificationFailed}
          resetErrorOnBlur={false}
        />
      </Form.Field>

      {isAccountVerified ? (
        <Button
          type="button"
          variant="outline"
          shape="pill"
          size="medium"
          fullWidth
          onClick={onEditButtonClick}
        >
          {t('button.edit')}
        </Button>
      ) : (
        <Button
          type="button"
          variant="solid"
          shape="pill"
          size="medium"
          fullWidth
          onClick={verifyAccount}
          disabled={isVerificationFailed}
        >
          {t('button.verify_account_holder')}
        </Button>
      )}

      <ErrorMessage />
    </div>
  )
}
